import type { PasswordAbout } from '@calling/shared';
import { useEffect, useId, useRef, useState, type Ref } from 'react';
import { EyeIcon, EyeOffIcon } from './icons';
import { PasswordStrength } from './PasswordStrength';

/**
 * A password input with a button that shows what was typed, for typing a
 * long password on a phone. `autoComplete` tells password managers what it
 * is: 'current-password' to fill one in, 'new-password' to offer to make and
 * save one. Never checked by spelling or capitalised by the keyboard.
 * `strength`, for a new password, shows the strength meter and the
 * requirements under it (PasswordStrength) in place of `hint`, for someone
 * with these details.
 */
export function PasswordField({
  label,
  value,
  onChange,
  onBlur,
  autoComplete,
  hint,
  error,
  autoFocus,
  inputRef,
  testId,
  strength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  autoComplete: 'current-password' | 'new-password';
  hint?: string;
  error?: string;
  autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  testId?: string;
  strength?: PasswordAbout;
}) {
  const id = useId();
  const [shown, setShown] = useState(false);
  const [focused, setFocused] = useState(false);
  const meter = useRef<HTMLDivElement>(null);
  // With the meter, the requirements stay under the field after a mistake, and the field is described by both.
  const described = strength ? (error ? `${id}-error ${id}-hint` : `${id}-hint`) : error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const errorLine = error ? (
    <span className="error" id={`${id}-error`} role="alert">
      {error}
    </span>
  ) : null;

  // On a phone the keyboard covers the lower part of the screen, where the
  // meter and the requirements are: while the field is typed in, they are
  // scrolled into what is left of it, as it opens and at each key. Where the
  // keyboard covers the page without making it smaller (iOS), the part it
  // covers is left out too.
  const metered = Boolean(strength);
  useEffect(() => {
    if (!metered || !focused) return;
    const view = window.visualViewport;
    const keepInView = () => {
      const block = meter.current;
      if (!block) return;
      const covered = view ? Math.max(0, window.innerHeight - view.offsetTop - view.height) : 0;
      block.style.scrollMarginBottom = `${covered + 8}px`;
      block.scrollIntoView({ block: 'nearest' });
    };
    // After the browser has scrolled the field itself into view.
    const frame = window.requestAnimationFrame(keepInView);
    view?.addEventListener('resize', keepInView);
    return () => {
      window.cancelAnimationFrame(frame);
      view?.removeEventListener('resize', keepInView);
    };
  }, [metered, focused, value]);

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="password-field">
        <input
          id={id}
          ref={inputRef}
          className="input"
          type={shown ? 'text' : 'password'}
          name={autoComplete === 'new-password' ? 'new-password' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={512}
          autoFocus={autoFocus}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
          data-testid={testId}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
        />
        <button
          type="button"
          className="password-field__toggle"
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          aria-controls={id}
          onClick={() => setShown(!shown)}
        >
          {shown ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
        </button>
      </div>
      {strength ? (
        <PasswordStrength password={value} about={strength} hintId={`${id}-hint`} refused={Boolean(error)} ref={meter}>
          {errorLine}
        </PasswordStrength>
      ) : error ? (
        errorLine
      ) : hint ? (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
